import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, X } from "lucide-react";

const TABS = ["Services", "Blog", "Showcase", "Contact", "Family", "Lotto Page", "Settings", "Account"];

const emptyService = {
  name: "", category: "cashback", tagline: "", description: "",
  how_it_works: "", pros: [], referral_url: "",
  average_monthly_earning: 0, icon: "sparkles", color: "#84A98C",
  featured: false, order: 0,
};
const emptyBlog = { title: "", excerpt: "", content: "", cover_image: "", published: true, tags: [] };
const emptyShowcase = { service_name: "", category: "Cashback", monthly_saving: 0, notes: "", order: 0 };

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("Services");

  if (ready && (!user || user.role !== "admin")) return <Navigate to="/admin" replace />;

  return (
    <main className="mds-container py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <span className="mds-badge">Admin</span>
          <h1 className="font-heading font-semibold text-3xl sm:text-4xl mt-2">Dashboard</h1>
          <p className="text-[#4A5D54] mt-1">Welcome back, {user?.name || "Matt"}.</p>
        </div>
        <button onClick={logout} className="mds-btn mds-btn-secondary" data-testid="logout-btn">
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[#E5E3DB] pb-4 mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
              tab === t ? "bg-[#84A98C] text-white border-transparent" : "bg-white text-[#4A5D54] border-[#E5E3DB] hover:border-[#84A98C]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Services" && <ServicesPanel />}
      {tab === "Blog" && <BlogPanel />}
      {tab === "Showcase" && <ShowcasePanel />}
      {tab === "Contact" && <ContactPanel />}
      {tab === "Family" && <FamilyPanel />}
      {tab === "Lotto Page" && <LottoContentPanel />}
      {tab === "Settings" && <SettingsPanel />}
      {tab === "Account" && <AccountPanel />}
    </main>
  );
}

function ServicesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/services").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/services/${data.id}`, data);
      else await api.post("/services", data);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...emptyService })} className="mds-btn mds-btn-primary" data-testid="new-service-btn">
          <Plus size={16} /> New service
        </button>
      </div>
      <div className="rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F0EFEA]/60 text-[#7A8C84] uppercase text-xs">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-right">£/mo</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E3DB]">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-[#4A5D54]">{r.category}</td>
                <td className="px-4 py-3 text-right font-num">£{r.average_monthly_earning?.toFixed(0)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(r)} className="p-2 hover:text-[#6B8F73]" data-testid={`edit-service-${r.slug}`}><Edit3 size={16} /></button>
                  <button onClick={() => del(r.id)} className="p-2 hover:text-red-600" data-testid={`delete-service-${r.slug}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ServiceModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
}

function ServiceModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({ ...initial, pros: (initial.pros || []).join("\n") });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...f,
      average_monthly_earning: parseFloat(f.average_monthly_earning) || 0,
      order: parseInt(f.order) || 0,
      pros: f.pros.split("\n").map((p) => p.trim()).filter(Boolean),
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="service-modal">
        <div className="p-6 border-b border-[#E5E3DB] flex items-center justify-between">
          <h3 className="font-heading font-semibold text-xl">{initial.id ? "Edit" : "New"} service</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Name"><input required className="mds-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="svc-name" /></Field>
          <Field label="Category">
            <select className="mds-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="svc-category">
              <option value="cashback">Cashback</option>
              <option value="reward-apps">Reward Apps</option>
              <option value="card-tools">Card Tools</option>
              <option value="surveys">Surveys</option>
            </select>
          </Field>
          <Field label="Tagline"><input className="mds-input" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></Field>
          <Field label="Description"><textarea rows={3} className="mds-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <Field label="How it works"><textarea rows={4} className="mds-input" value={f.how_it_works} onChange={(e) => setF({ ...f, how_it_works: e.target.value })} /></Field>
          <Field label="Pros (one per line)"><textarea rows={4} className="mds-input" value={f.pros} onChange={(e) => setF({ ...f, pros: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Referral URL"><input className="mds-input" value={f.referral_url} onChange={(e) => setF({ ...f, referral_url: e.target.value })} /></Field>
            <Field label="Avg £/month"><input type="number" className="mds-input" value={f.average_monthly_earning} onChange={(e) => setF({ ...f, average_monthly_earning: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Icon (lucide-react name)"><input className="mds-input" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} /></Field>
            <Field label="Order"><input type="number" className="mds-input" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} />
            Featured on homepage
          </label>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="mds-btn mds-btn-secondary">Cancel</button>
            <button type="submit" className="mds-btn mds-btn-primary" data-testid="svc-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BlogPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/blog").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/blog/${data.id}`, data);
      else await api.post("/blog", data);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete post?")) return;
    try { await api.delete(`/blog/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...emptyBlog })} className="mds-btn mds-btn-primary" data-testid="new-post-btn"><Plus size={16} /> New post</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="mds-card">
            <div className="flex justify-between gap-2">
              <div>
                <div className="text-xs text-[#7A8C84]">{new Date(r.created_at).toLocaleDateString("en-GB")}</div>
                <h3 className="font-heading font-semibold mt-1">{r.title}</h3>
                <p className="text-sm text-[#4A5D54] mt-2 line-clamp-2">{r.excerpt}</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => setEditing(r)} className="p-2 hover:text-[#6B8F73]"><Edit3 size={16} /></button>
                <button onClick={() => del(r.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <BlogModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
}

function BlogModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({ ...initial, tags: (initial.tags || []).join(", ") });
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="blog-modal">
        <div className="p-6 border-b border-[#E5E3DB] flex items-center justify-between">
          <h3 className="font-heading font-semibold text-xl">{initial.id ? "Edit" : "New"} post</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Title"><input required className="mds-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Excerpt"><textarea rows={2} className="mds-input" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} /></Field>
          <Field label="Content"><textarea rows={10} className="mds-input" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} /></Field>
          <Field label="Cover image URL"><input className="mds-input" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} /></Field>
          <Field label="Tags (comma separated)"><input className="mds-input" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} />
            Published
          </label>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="mds-btn mds-btn-secondary">Cancel</button>
            <button type="submit" className="mds-btn mds-btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShowcasePanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/showcase").then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    try {
      if (data.id) await api.put(`/showcase/${data.id}`, data);
      else await api.post("/showcase", data);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete item?")) return;
    try { await api.delete(`/showcase/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...emptyShowcase })} className="mds-btn mds-btn-primary"><Plus size={16} /> New item</button>
      </div>
      <div className="rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F0EFEA]/60 text-[#7A8C84] uppercase text-xs">
            <tr><th className="px-4 py-3">Service</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-right">£/mo</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E3DB]">
                <td className="px-4 py-3 font-medium">{r.service_name}</td>
                <td className="px-4 py-3">{r.category}</td>
                <td className="px-4 py-3 text-right font-num">£{r.monthly_saving.toFixed(0)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(r)} className="p-2 hover:text-[#6B8F73]"><Edit3 size={16} /></button>
                  <button onClick={() => del(r.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ShowcaseModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
}

function ShowcaseModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({ ...initial });
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...f, monthly_saving: parseFloat(f.monthly_saving) || 0, order: parseInt(f.order) || 0 });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E3DB] flex items-center justify-between">
          <h3 className="font-heading font-semibold text-xl">{initial.id ? "Edit" : "New"} item</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Service name"><input required className="mds-input" value={f.service_name} onChange={(e) => setF({ ...f, service_name: e.target.value })} /></Field>
          <Field label="Category"><input className="mds-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <Field label="Monthly saving £"><input type="number" step="0.01" className="mds-input" value={f.monthly_saving} onChange={(e) => setF({ ...f, monthly_saving: e.target.value })} /></Field>
          <Field label="Notes"><input className="mds-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <Field label="Order"><input type="number" className="mds-input" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="mds-btn mds-btn-secondary">Cancel</button>
            <button type="submit" className="mds-btn mds-btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/contact/submissions").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete message from ${name}?`)) return;
    try { await api.delete(`/contact/submissions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="contact-panel">
      {rows.length === 0 && <p className="text-[#7A8C84]">No messages yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="mds-card" data-testid={`contact-msg-${r.id}`}>
          <div className="flex flex-wrap justify-between gap-2 mb-2 items-start">
            <div>
              <div className="font-medium">{r.name}</div>
              <a
                href={`mailto:${r.email}?subject=${encodeURIComponent(`Re: your message to Matt's Daily Savers`)}&body=${encodeURIComponent(`Hi ${r.name},\n\n`)}`}
                className="mds-link text-sm"
                data-testid={`contact-email-${r.id}`}
              >
                {r.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#7A8C84]">{new Date(r.created_at).toLocaleString("en-GB")}</span>
              <button
                onClick={() => del(r.id, r.name)}
                className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Delete message"
                data-testid={`delete-contact-${r.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <p className="text-[#4A5D54] whitespace-pre-wrap">{r.message}</p>
        </div>
      ))}
    </div>
  );
}

function SettingsPanel() {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setHtml(r.data.adsense_head_html || ""));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    try {
      await api.put("/settings", { adsense_head_html: html });
      toast.success("Head snippet saved — reload any page to see it live.");
      setSaved(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mds-card max-w-3xl" data-testid="settings-panel">
      <h2 className="font-heading font-semibold text-xl">Head / AdSense snippet</h2>
      <p className="text-sm text-[#4A5D54] mt-2 leading-relaxed">
        Paste the Google AdSense (or any) HTML snippet here. Any <code className="bg-[#F0EFEA] px-1 rounded">&lt;script&gt;</code> and{" "}
        <code className="bg-[#F0EFEA] px-1 rounded">&lt;meta&gt;</code> tags will be injected into the <strong>&lt;head&gt;</strong> of every page automatically.
      </p>
      <form onSubmit={save} className="mt-5" data-testid="settings-form">
        <textarea
          rows={10}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          className="mds-input font-mono text-xs"
          placeholder={`<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXXXXXXXXXX" crossorigin="anonymous"></script>`}
          data-testid="adsense-textarea"
        />
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <p className="text-xs text-[#7A8C84]">
            Tip: paste the full snippet Google gives you — including the <code>&lt;script&gt;</code> tag.
          </p>
          <div className="flex items-center gap-3">
            {saved && <span className="text-sm text-[#6B8F73]">Saved ✓</span>}
            <button type="submit" disabled={loading} className="mds-btn mds-btn-primary" data-testid="settings-save">
              {loading ? "Saving…" : "Save snippet"}
            </button>
          </div>
        </div>
      </form>

      <div className="mt-8 pt-6 border-t border-[#E5E3DB]">
        <h3 className="font-heading font-semibold">Preview of what's currently live</h3>
        <pre className="mt-3 p-4 bg-[#F0EFEA] rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-words" data-testid="settings-preview">
{html || "(nothing injected)"}
        </pre>
      </div>
    </div>
  );
}

function FamilyPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/family-users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        const payload = { email: data.email, name: data.name };
        if (data.password) payload.password = data.password;
        await api.put(`/admin/family-users/${data.id}`, payload);
      } else {
        await api.post("/admin/family-users", { email: data.email, name: data.name, password: data.password });
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const del = async (id, email) => {
    if (!window.confirm(`Delete family user ${email}? They will lose access to /lotto.`)) return;
    try { await api.delete(`/admin/family-users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="family-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-semibold text-xl">Family / syndicate members</h2>
          <p className="text-sm text-[#4A5D54] mt-1">
            These users can sign in at <code className="bg-[#F0EFEA] px-1 rounded">/lotto</code> to see the syndicate page. They cannot access the admin panel.
          </p>
        </div>
        <button onClick={() => setEditing({ email: "", name: "", password: "" })} className="mds-btn mds-btn-primary" data-testid="new-family-btn">
          <Plus size={16} /> New member
        </button>
      </div>
      <div className="rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F0EFEA]/60 text-[#7A8C84] uppercase text-xs">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Created</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="4" className="px-4 py-6 text-center text-[#7A8C84]">No family members yet — add the first one.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E3DB]">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-[#4A5D54]">{r.email}</td>
                <td className="px-4 py-3 text-[#7A8C84] text-xs">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...r, password: "" })} className="p-2 hover:text-[#6B8F73]" data-testid={`edit-family-${r.id}`}><Edit3 size={16} /></button>
                  <button onClick={() => del(r.id, r.email)} className="p-2 hover:text-red-600" data-testid={`delete-family-${r.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <FamilyModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function FamilyModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({ ...initial });
  const isNew = !initial.id;
  const submit = (e) => {
    e.preventDefault();
    if (isNew && (!f.password || f.password.length < 8)) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    onSave(f);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="family-modal">
        <div className="p-6 border-b border-[#E5E3DB] flex items-center justify-between">
          <h3 className="font-heading font-semibold text-xl">{isNew ? "New" : "Edit"} member</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Name">
            <input required className="mds-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="family-name" />
          </Field>
          <Field label="Email">
            <input type="email" required className="mds-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="family-email" />
          </Field>
          <Field label={isNew ? "Password (min 8 chars)" : "New password (leave blank to keep current)"}>
            <input
              type="text"
              className="mds-input"
              value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
              placeholder={isNew ? "Give them something memorable" : "Leave blank to keep current"}
              data-testid="family-password"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="mds-btn mds-btn-secondary">Cancel</button>
            <button type="submit" className="mds-btn mds-btn-primary" data-testid="family-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LottoContentPanel() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/lotto/content").then((r) => setContent({ sections: [], ...r.data })); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: content.title || "Family Lottery Syndicate",
        intro: content.intro || "",
        total_winnings: parseFloat(content.total_winnings) || 0,
        sections: (content.sections || []).map((s) => ({ heading: s.heading || "", body: s.body || "" })),
        draws: (content.draws || []).map((d) => ({
          date: d.date || "",
          game: d.game || "Lotto",
          numbers: d.numbers || "",
          prize: parseFloat(d.prize) || 0,
          notes: d.notes || "",
        })),
      };
      const { data } = await api.put("/lotto/content", payload);
      setContent({ sections: [], ...data });
      toast.success("Lotto page updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => setContent({ ...content, sections: [...(content.sections || []), { heading: "", body: "" }] });
  const updateSection = (i, patch) => {
    const next = [...(content.sections || [])];
    next[i] = { ...next[i], ...patch };
    setContent({ ...content, sections: next });
  };
  const removeSection = (i) => {
    if (!window.confirm("Remove this section?")) return;
    const next = [...(content.sections || [])];
    next.splice(i, 1);
    setContent({ ...content, sections: next });
  };
  const moveSection = (i, dir) => {
    const next = [...(content.sections || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setContent({ ...content, sections: next });
  };

  const addDraw = () => {
    setContent({
      ...content,
      draws: [
        { date: new Date().toISOString().slice(0, 10), game: "Lotto", numbers: "", prize: 0, notes: "" },
        ...(content.draws || []),
      ],
    });
  };
  const updateDraw = (i, patch) => {
    const next = [...content.draws];
    next[i] = { ...next[i], ...patch };
    setContent({ ...content, draws: next });
  };
  const removeDraw = (i) => {
    if (!window.confirm("Remove this draw?")) return;
    const next = [...content.draws];
    next.splice(i, 1);
    setContent({ ...content, draws: next });
  };

  if (!content) return <p className="text-[#7A8C84]">Loading…</p>;

  return (
    <form onSubmit={save} className="space-y-6 max-w-4xl" data-testid="lotto-content-panel">
      <div className="mds-card">
        <h2 className="font-heading font-semibold text-xl">Lotto page content</h2>
        <p className="text-sm text-[#4A5D54] mt-1">Shown at <code className="bg-[#F0EFEA] px-1 rounded">/lotto</code> once a family member signs in.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <Field label="Page title">
            <input className="mds-input" value={content.title || ""} onChange={(e) => setContent({ ...content, title: e.target.value })} data-testid="lotto-title" />
          </Field>
          <Field label="Total winnings so far (£)">
            <input type="number" step="0.01" className="mds-input" value={content.total_winnings ?? 0} onChange={(e) => setContent({ ...content, total_winnings: e.target.value })} data-testid="lotto-total" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Intro (short paragraph shown under the title)">
            <textarea rows={3} className="mds-input" value={content.intro || ""} onChange={(e) => setContent({ ...content, intro: e.target.value })} data-testid="lotto-intro" />
          </Field>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-lg">Sections</h3>
            <p className="text-xs text-[#7A8C84] mt-0.5">Add as many as you like — each has its own heading and paragraph, and they render as separate cards on the page.</p>
          </div>
          <button type="button" onClick={addSection} className="mds-btn mds-btn-primary text-sm py-2 px-3" data-testid="lotto-add-section">
            <Plus size={16} /> Add section
          </button>
        </div>
        {(content.sections || []).length === 0 && (
          <p className="text-[#7A8C84] text-sm mb-2">No sections yet — click "Add section" to break the intro into readable chunks.</p>
        )}
        <div className="space-y-3">
          {(content.sections || []).map((s, i) => (
            <div key={i} className="mds-card" data-testid={`lotto-section-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-[#7A8C84]">Section {i + 1}</span>
                <div className="flex-1" />
                <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" title="Move up">↑</button>
                <button type="button" onClick={() => moveSection(i, 1)} disabled={i === (content.sections.length - 1)} className="p-1 disabled:opacity-30" title="Move down">↓</button>
                <button type="button" onClick={() => removeSection(i)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Remove"><Trash2 size={14} /></button>
              </div>
              <Field label="Heading">
                <input className="mds-input" value={s.heading || ""} onChange={(e) => updateSection(i, { heading: e.target.value })} placeholder="e.g. How it works" />
              </Field>
              <Field label="Body">
                <textarea rows={4} className="mds-input" value={s.body || ""} onChange={(e) => updateSection(i, { body: e.target.value })} placeholder="Blank lines between paragraphs will be preserved." />
              </Field>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-lg">Draws</h3>
          <button type="button" onClick={addDraw} className="mds-btn mds-btn-primary text-sm py-2 px-3" data-testid="lotto-add-draw">
            <Plus size={16} /> Add draw
          </button>
        </div>
        {(content.draws || []).length === 0 && (
          <p className="text-[#7A8C84] text-sm">No draws yet — click "Add draw" to record the first one.</p>
        )}
        <div className="space-y-4">
          {(content.draws || []).map((d, i) => (
            <div key={i} className="mds-card">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Field label="Date">
                  <input type="date" className="mds-input" value={d.date || ""} onChange={(e) => updateDraw(i, { date: e.target.value })} />
                </Field>
                <Field label="Game">
                  <input className="mds-input" value={d.game || ""} onChange={(e) => updateDraw(i, { game: e.target.value })} placeholder="Lotto, EuroMillions…" />
                </Field>
                <Field label="Numbers (comma separated)">
                  <input className="mds-input" value={d.numbers || ""} onChange={(e) => updateDraw(i, { numbers: e.target.value })} placeholder="3, 12, 24, 31, 45, 51" />
                </Field>
                <Field label="Prize (£)">
                  <input type="number" step="0.01" className="mds-input" value={d.prize ?? 0} onChange={(e) => updateDraw(i, { prize: e.target.value })} />
                </Field>
              </div>
              <Field label="Notes">
                <textarea rows={2} className="mds-input" value={d.notes || ""} onChange={(e) => updateDraw(i, { notes: e.target.value })} />
              </Field>
              <div className="flex justify-end mt-2">
                <button type="button" onClick={() => removeDraw(i)} className="text-red-600 text-sm inline-flex items-center gap-1 hover:underline">
                  <Trash2 size={14} /> Remove draw
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={loading} className="mds-btn mds-btn-primary" data-testid="lotto-save">
          {loading ? "Saving…" : "Save lotto page"}
        </button>
      </div>
    </form>
  );
}

function AccountPanel() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPw) { toast.error("Please enter your current password to confirm changes."); return; }
    if (newPw && newPw !== confirmPw) { toast.error("New passwords don't match."); return; }
    if (newPw && newPw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (email === user.email && !newPw) { toast.error("Nothing to update."); return; }

    setLoading(true);
    try {
      const payload = { current_password: currentPw };
      if (email && email !== user.email) payload.new_email = email;
      if (newPw) payload.new_password = newPw;

      const { data } = await api.put("/auth/profile", payload);
      if (data.access_token) localStorage.setItem("mds_token", data.access_token);
      toast.success("Your account details have been updated.");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      // Refresh page state so header + auth context reflect the new email
      window.location.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mds-card max-w-2xl" data-testid="account-panel">
      <h2 className="font-heading font-semibold text-xl">Account details</h2>
      <p className="text-sm text-[#4A5D54] mt-2 leading-relaxed">
        Change your admin email and/or password. You'll need to confirm your current password to save any changes.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="account-form">
        <Field label="Admin email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mds-input"
            data-testid="account-email-input"
          />
        </Field>

        <div className="pt-4 border-t border-[#E5E3DB]">
          <h3 className="font-heading font-semibold text-lg">Change password</h3>
          <p className="text-xs text-[#7A8C84] mt-1">Leave blank to keep your current password.</p>
        </div>

        <Field label="New password">
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="mds-input"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            data-testid="account-new-password"
          />
        </Field>
        <Field label="Confirm new password">
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            className="mds-input"
            autoComplete="new-password"
            data-testid="account-confirm-password"
          />
        </Field>

        <div className="pt-4 border-t border-[#E5E3DB]">
          <Field label="Current password (required to confirm)">
            <input
              type="password"
              required
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="mds-input"
              autoComplete="current-password"
              data-testid="account-current-password"
            />
          </Field>
        </div>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={loading} className="mds-btn mds-btn-primary" data-testid="account-save">
            {loading ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#1C2321]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
